import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { Box, IconButton } from '@mui/material';
import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import Routes, { DELIVERY_ID_PARAM } from 'routes';
import { useDeliveries } from 'api/hooks/useDeliveries';
import Theme from 'App.theme';
import DeliveryList from 'components/DeliveryListPage/components/DeliveryList/DeliveryList';
import { addBusinessDays, formatDateToYYYYMMDD } from 'utils/helpers/DateTimeFormat.helpers';
import { useActionItemsFilters } from './hooks/useActionItemsFilters';
import { CheckInDeliveryDialog } from 'components/CheckInDeliveryDialog/CheckInDeliveryDialog';
import { HistoricalSwitch } from 'api/models/Deliveries';
import { useDeliveryClickHandler } from 'hooks/useDeliveryClickHandler';

export enum TableTypes {
  DEFAULT = 'DEFAULT',
  RECEIVE_OVERDUE = 'RECEIVE_OVERDUE',
  CHECK_IN_OVERDUE = 'CHECK_IN_OVERDUE',
  CHECK_OUT_OVERDUE = 'CHECK_OUT_OVERDUE',
  ALL = 'ALL',
}

// Current date and time in milliseconds
const dateNow = new Date();

// Calculate the timestamps for 72 hours after the current date and the beginning of time
const dateNowPlus72Hours = addBusinessDays(dateNow, 2);
const beginningOfTime = 0;

// Format the dates to 'YYYY-MM-DD'
const formattedBeginningOfTime = formatDateToYYYYMMDD(beginningOfTime);
const formattedDateNowPlus72Hours = formatDateToYYYYMMDD(dateNowPlus72Hours);

type OverdueReceiveDeliveriesTableProps = {
  expandedTable: TableTypes;
  handleToggleExpand: (type: TableTypes) => void;
};

const OverdueReceiveDeliveriesTable = ({ expandedTable, handleToggleExpand }: OverdueReceiveDeliveriesTableProps) => {
  const location = useLocation();
  const isPastDeliveries = location.pathname === Routes.PastDeliveries.path;

  const [filters, _setFilters] = useActionItemsFilters();

  // This is any delivery that is in 'Check in' or 'Partially checked out' status, where the date is 24 hours or greater beyond the surgery date.
  const { data: overdueReceiveDeliveries, isLoading: overdueReceiveDeliveriesLoading } = useDeliveries({
    ...filters,
    status: 'OUTBOUND',
    lower_surgery_date: formattedBeginningOfTime,
    upper_surgery_date: formattedDateNowPlus72Hours,
    historical: HistoricalSwitch.Active,
  });

  const { activeDeliveryForCheckInDeliveryDialog, handleDeliveryClick, setActiveDeliveryForCheckInDeliveryDialog } =
    useDeliveryClickHandler();

  const navigate = useNavigate();

  const handleRowClick = useCallback(
    async (deliveryId: number) => {
      const delivery = overdueReceiveDeliveries?.result.find(
        deliverySummary => deliverySummary.deliveryId === deliveryId,
      );

      if (!delivery) {
        throw new Error('Delivery not found');
      }

      handleDeliveryClick(deliveryId, delivery.status, !delivery.surgeryReference || !delivery.patientInitials);
    },
    [handleDeliveryClick, overdueReceiveDeliveries?.result],
  );

  const pageSize = overdueReceiveDeliveries?.metadata.count || 0;
  const pageSizeOptions = useMemo(() => [pageSize], [pageSize]);

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#ffe6bc',
          height: '50px',
          color: Theme.colors.orange,
          fontWeight: 'medium',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: '20px',
          padding: '5px',
        }}
      >
        <p style={{ margin: 0, paddingLeft: '10px', fontSize: '20px' }}>
          There are{' '}
          <b>
            <u style={{ fontSize: '25px', padding: '5px' }}>{overdueReceiveDeliveries?.metadata.total || 'no'}</u>
          </b>{' '}
          Deliveries to be Received {overdueReceiveDeliveries?.metadata.total == 0 && 'today'}
        </p>
        <div>
          {overdueReceiveDeliveries?.metadata.total !== 0 && (
            <IconButton
              name="clear"
              title="Clear"
              sx={{ paddingRight: 0 }}
              onClick={() => handleToggleExpand(TableTypes.RECEIVE_OVERDUE)}
              style={{ paddingRight: '10px' }}
            >
              {expandedTable == TableTypes.RECEIVE_OVERDUE ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
            </IconButton>
          )}
        </div>
      </Box>

      {overdueReceiveDeliveries?.metadata.total !== 0 &&
        (expandedTable == TableTypes.RECEIVE_OVERDUE || expandedTable == TableTypes.ALL) && (
          <DeliveryList
            sx={{ flex: 1, overflow: 'hidden' }} // This ensures the list fills up available vertical space
            isActionItemsTable={true}
            data={overdueReceiveDeliveries?.result || []}
            numPages={1}
            curPage={1}
            pageSizeOptions={pageSizeOptions}
            pageSize={pageSize}
            isPastDeliveries={isPastDeliveries}
            loading={overdueReceiveDeliveriesLoading}
            onRowClick={handleRowClick}
            tableType={TableTypes.RECEIVE_OVERDUE}
          />
        )}

      {activeDeliveryForCheckInDeliveryDialog && (
        <CheckInDeliveryDialog
          deliveryId={activeDeliveryForCheckInDeliveryDialog.toString()}
          onClose={() => {
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
          onCancel={() => {
            // we still want to open the delivery but not the check in workflow
            navigate(Routes.CheckIn.path.replace(DELIVERY_ID_PARAM, activeDeliveryForCheckInDeliveryDialog.toString()));
            setActiveDeliveryForCheckInDeliveryDialog(null);
          }}
        />
      )}
    </>
  );
};

export default OverdueReceiveDeliveriesTable;
